<template>
  <LoadingSpinner v-if="!!isLoading" />
  <div v-else class="show-budget">
    <ProjectHeader :project="project">
      <template #actions>
        <BaseButton v-if="budget.id" variant="accent" :disable="mode === 'edit'" @click="mode = 'edit'">Edit Budget</BaseButton>
        <BaseButton v-else :to="{ name: 'CreateBudget', params: { id: project.id }}" variant="accent">Create Budget</BaseButton>
      </template>
    </ProjectHeader>
    <ProjectSubNavigation :project="project" />

    <div v-if="budget.id">
      <div class="mb-8">
        <JobCostAnalytics
          :materials-budget="materialBudget"
          :actual-material-cost="actualMaterialCost"
          :actual-labor-cost="actualLaborCost"
          :labors-budget="laborBudget"
          :total-budget-expenses="totalBudget"
          :total-budget-revenue="totalBudgetRevenue"
        />

        <div
          v-if="budget.id && project.status === 'work_in_progress'"
          class="grid grid-cols-3 gap-4 justify-items-center mt-4"
        >
          <div>
            <BaseButton
              icon-class="i-heroicons-calendar"
              text="Schedule tasks"
              icon-position="left"
            />
          </div>
          <div>
            <BaseButton
              :to="{ name:'CreateWorkOrder', query: { projectId: project.id } }"
              text="Work order"
              icon-class="i-heroicons-wrench"
              icon-position="left"
            />
          </div>
          <div>
            <BaseButton
              :to="{ name:'CreatePurchaseOrder', query: { projectId: project.id } }"
              text="Purchase order"
              icon-class="i-heroicons-shopping-cart"
              icon-position="left"
            />
          </div>
        </div>
      </div>

      <div
        class="block text-white px-4 py-2"
        :class="[mode === 'edit' ? 'bg-warning' : 'bg-success/90']"
      >
        <div class="flex items-center gap-2">
          <span>{{ mode === 'edit' ? 'Edit Mode' : 'Active Mode' }}</span>

          <!-- three dot pulsing -->
          <span
            v-if="mode === 'edit' && autoSaving"
            class="flex-1 i-heroicons-ellipsis-horizontal animate-pulse font-bold size-6 inline-block"
          />
        </div>
      </div>

      <!-- show budget -->
      <div v-if="mode === 'active'">
        <div
          v-for="(category, categoryIndex) in sortedCategories"
          :key="`category-${categoryIndex}`"
          class="budget-item-container mb-2"
        >
          <BaseCollapse>
            <template #header>
              <span>{{ category.name }}</span>
            </template>

            <div
              v-for="(item, itemIndex) in filterBudgetItemByCategory(category)"
              :key="itemIndex"
              :class="{ 'mt-4': itemIndex > 0, 'opacity-50': !item.selected }"
            >

              <span class="text-base font-bold block">
                {{ item.title }}
              </span>
              <span v-if="item.isCreditLineItem" class="mb-2 inline-block">
                <CreditBadge />
              </span>

              <div class="grid grid-cols-3 md:grid-cols-5 px-4 mt-4">
                <!-- material -->
                <div class="text-sm flex flex-col gap-9 md:gap-4">
                  <span>Materials</span>
                  <span>Labor</span>
                </div>

                <!-- progress bar -->
                <div class="hidden md:col-span-2 md:flex md:flex-col md:gap-4 md:w-3/4">
                  <div class="my-auto">
                    <BaseProgressBar
                      :progress="getProgress(toNumber(item.materialsBudget), getItemActualMaterialCost(item.id))"
                    />
                  </div>

                  <div class="my-auto">
                    <BaseProgressBar
                      :progress="getProgress(toNumber(item.laborBudget), getItemActualLaborCost(item.id))"
                    />
                  </div>
                </div>

                <!-- budget -->
                <div class="text-sm flex flex-col gap-4">
                  <div>
                    <span class="block md:inline-block md:mr-2">Budget</span>
                    <span class="block md:inline-block md:mr-2">{{ currency(item.materialsBudget) }}</span>
                  </div>
                  <div>
                    <span class="block md:inline-block md:mr-2">Budget</span>
                    <span class="block md:inline-block md:mr-2">{{ currency(item.laborBudget) }}</span>
                  </div>
                </div>

                <!-- Actual -->
                <div class="text-sm flex flex-col gap-4">
                  <div>
                    <span class="block md:inline-block md:mr-2">Actual</span>
                    <span class="block md:inline-block md:mr-2">{{ currency(getItemActualMaterialCost(item.id)) }}</span>
                  </div>
                  <div>
                    <span class="block md:inline-block md:mr-2">Actual</span>
                    <span class="block md:inline-block md:mr-2">{{ currency(getItemActualLaborCost(item.id)) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </BaseCollapse>
        </div>
      </div>

      <!-- edit budget -->
      <div v-if="mode === 'edit'">
        <form.Field
          name="categories"
        >
          <template #default="{ field: categoriesField }">
            <div>
              <BaseDraggable
                :model-value="(categoriesField.state.value as any[])"
                item-key="key"
                items-id-prefix="key"
                @update:model-value="(values) => setCategories(values, categoriesField)"
              >
                <template #delete-button="{ element, index }">
                  <span
                    class="i-heroicons-trash text-error"
                    :class="categoriesField.form.getFieldValue('categories').length === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-error cursor-pointer'"
                    :disabled="categoriesField.form.getFieldValue('categories').length === 1"
                    @click="categoriesField.form.getFieldValue('categories').length === 1 ? '' : deleteCategory(filterBudgetItemByCategory(element), index, categoriesField)"
                  />
                </template>

                <template #collapsed-text="{ element }">
                  <div class="flex item-center py-2">
                    <span class="font-bold">{{ element.name }}</span>
                  </div>
                </template>

                <template #collapse-button="{ open:openCat, element, toggleFn: toggleFnCat }">
                  <span
                    class="text-gray-600 size-4"
                    :class="openCat ? 'i-mdi-unfold-less-horizontal' : 'i-mdi-unfold-more-horizontal'"
                    @click="toggleFnCat(element.id);"
                  />
                </template>

                <template #item-content="{ element: category, index: categoryIndex, open: openCat }">
                  <div :id="`key${categoryIndex}`">
                    <div>
                      <form.Field
                        :key="`field-${categoryIndex}`"
                        :name="`categories[${categoryIndex}].name`"
                        :validators="{
                          onChangeListenTo: ['categories'],
                          onChange: yup.string().required('Category is required')
                        }"
                      >
                        <template #default="{ field }">
                          <BaseInput
                            :name="field.name"
                            :model-value="category.name"
                            placeholder="Category"
                            class="mt-4"
                            :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                            @blur="updateBudgetItemsCategory($event, category.name, categoryIndex, field)"
                          />
                        </template>
                      </form.Field>
                    </div>

                    <!-- line items -->
                    <BaseDraggable
                      :key="`line-items-${categoryIndex}`"
                      :model-value="sortedLineItems(category)"
                      item-key="id"
                      :items-id-prefix="`id${category.key}`"
                      @update:model-value="reOrderItems"
                      @delete="(event) => deleteItem(event.element.id)"
                    >
                      <template #delete-button="{ element }">
                        <span
                          class="i-heroicons-trash text-error"
                          @click="deleteItem(element.id)"
                        />
                      </template>
                      <template #collapse-button="{ open, element, toggleFn }">
                        <span
                          class="text-gray-600 size-4"
                          :class="open ? 'i-mdi-unfold-less-horizontal' : 'i-mdi-unfold-more-horizontal'"
                          @click="toggleFn(element.id);"
                        />
                      </template>

                      <template #collapsed-text="{ element }">
                        <div class="flex item-center py-2">
                          <span class="font-bold">{{ element.value.title }}</span>
                        </div>
                      </template>

                      <template #item-content="{ element: item, index, open }">
                        <div :id="`id${category.key}${index}`">
                          <CreateBudgetItem
                            :id="item.value.id"
                            :key="`item-${category.id}-${item.weight}`"
                            v-model="item.value"
                            :is-saving="false"
                            :is-open="open && openCat"
                            @can-submit="(value) => itemsValidations[item.value.id]=value"
                            @on-delete="deleteItem(item.value.id)"
                          />
                        </div>
                      </template>
                    </BaseDraggable>

                    <hr class="my-6">

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <BaseButton
                          variant="base"
                          type="button"
                          class="w-full"
                          style="box-shadow: none !important;"
                          @click="addBudgetItem(categoryIndex, category)"
                        >
                          <div class="flex items-center justify-center  gap-2">
                            <span class="i-heroicons-plus-circle size-8" />
                            <span class="text-lg">Add line item</span>
                          </div>
                        </BaseButton>
                      </div>

                      <div>
                        <BaseButton
                          data-modal="v-b-modal.budgetTemplateModal"
                          variant="base"
                          type="button"
                          class="w-full"
                          style="box-shadow: none !important;"
                          @click.prevent="showTemplatesModal(category)"
                        >
                          <div class="flex items-center justify-center  gap-2">
                            <span class="i-heroicons-plus-circle size-8" />
                            <span class="text-lg">Add from template</span>
                          </div>
                        </BaseButton>
                      </div>
                    </div>
                  </div>
                </template>
              </BaseDraggable>

              <div class="mt-4">
                <BaseButton
                  variant="base"
                  type="button"
                  class="w-full"
                  style="box-shadow: none !important;"
                  @click.prevent="addBudgetItemCategory(categoriesField)"
                >
                  <span class="i-heroicons-plus-circle size-8" />
                  <br>
                  <p class="text-lg">Add category</p>
                </BaseButton>
              </div>
            </div>
          </template>
        </form.Field>
      </div>

      <hr class="my-4">
      <form.Subscribe>
        <template #default="{ canSubmit, isSubmitting }">
          <div class="grid items-center gap-4" :class="mode === 'edit' ? 'grid-cols-2' : 'grid-cols-1'">
            <!-- last auto-saving indicator -->
            <div
              v-if="mode === 'edit'"
              class="text-sm text-gray-500"
            >
              <div
                v-if="isAutoSaveEnabled"
                class="flex items-center gap-2"
              >
                <span>Last auto-saved at:</span>
                <span v-if="!autoSaving" class="font-bold">
                  {{ lastAutoSave ? lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}
                </span>
                <span v-if="autoSaving" class="i-heroicons-ellipsis-horizontal animate-pulse font-bold size-6 inline-block" />
              </div>

              <div v-else class="text-gray-500 font-bold">
                Auto save is disabled
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 flex-1">
              <BaseButton
                v-if="mode === 'active'"
                text="Delete"
                variant="danger"
                @click="showBudgetDeleteConfirmation = true"
              />

              <BaseButton
                key="budget-mode"
                :text="mode === 'edit' ? 'Make Active' : 'Edit Mode'"
                variant="secondary"
                :disabled="mode === 'edit' && !canSubmit || isSubmitting || !isBudgetItemsFormsValid || isNotAllowedStatus || !allCategoriesHaveItems"
                :class="{ 'cursor-not-allowed opacity-50': mode === 'edit' && !canSubmit || isSubmitting || !isBudgetItemsFormsValid || !allCategoriesHaveItems }"
                @click="toggleBudgetMode"
              />

              <BaseButton
                v-if="mode === 'edit'"
                :disabled="isSubmitting || isNotAllowedStatus"
                text="Cancel"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting || isNotAllowedStatus }"
                @click="cancel"
              />

              <BaseButton
                v-if="mode === 'edit'"
                :disabled="!canSubmit || !isBudgetItemsFormsValid || isSubmitting || isNotAllowedStatus || !allCategoriesHaveItems"
                text="Save"
                type="submit"
                variant="primary"
                :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting || !isBudgetItemsFormsValid || isNotAllowedStatus || !allCategoriesHaveItems }"
                @click="form.handleSubmit"
              />
            </div>
          </div>
        </template>
      </form.Subscribe>
    </div>

    <div v-else class="flex flex-col items-center justify-center mt-12">
      <span class="i-heroicons-folder-open size-10 text-muted" />
      <h2 class="mb-2">No Budget</h2>
      <p class="mb-4">Get started by creating a new budget.</p>
      <BaseButton
        :to="{ name: 'CreateBudget', params: { id: project.id } }"
        varient="primary"
        icon-class="i-heroicons-plus-circle"
        text="New Budget"
      />
    </div>
  </div>

  <Modal
    id="budgetTemplateModal"
    v-model="showModal"
    title="Apply Budget Template"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="w-full md:w-[80%] lg:w-[70%] xl:w-[60%]"
  >
    <template #body>
      <BudgetItemTemplates
        @on-buget-template-selected="bugetTemplateSelected"
      />
    </template>
  </Modal>

  <Modal
    id="delete-budget-confirmation-modal"
    v-model="showBudgetDeleteConfirmation"
    title="Delete Budget"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
  >
    <template #body>
      <div class="mt-2">
        <p class="text-base mb-3">Are you sure you want to delete this budget?</p>
        <p class="text-sm mb-2">This will also delete:</p>
        <ul class="text-sm list-disc ml-6">
          <li>The proposal (if it exists)</li>
          <li>All work orders</li>
          <li>All purchase orders</li>
          <li>All invoices</li>
          <li>All bills</li>
        </ul>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 flex px-6 justify-end gap-2">
        <BaseButton
          text="Cancel"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="showBudgetDeleteConfirmation = false"
        />

        <BaseButton
          variant="danger"
          :disabled="isDeleting"
          :class="{ 'cursor-not-allowed opacity-50': isDeleting }"
          @click="deleteBudget({budget, customerId: project.customerId})"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Confirm</span>
            <span
              v-if="isDeleting"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import {
  extend,
  filter,
  findWhere,
  omit,
  uniqueId
} from 'underscore'

import {
  BudgetItem
} from '@/models/budget'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CreateBudgetItem from '../components/CreateBudgetItem.vue'
import JobCostAnalytics from '../components/JobCostAnalytics.vue'
import BudgetItemTemplates from '../components/BudgetItemTemplates.vue'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import { WorkOrder } from '@/models/workOrder'
import { PROJECT_STATUS } from '@/models/project'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useHead } from '@unhead/vue'
import { Budget } from '@/models/budget'
import { ref, computed, watch, reactive, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import BudgetService from '@/services/BudgetService'
import PurchaseOrderService from '@/services/PurchaseOrderService'
import WorkOrderService from '@/services/WorkOrderService'
import { currency, toNumber } from '@/utils'
import { PurchaseOrder } from '@/models/purchaseOrder'
import BaseProgressBar from '@/components/base/BaseProgressBar.vue'
import BaseDraggable from '@/components/base/BaseDraggable.vue'
import Modal from '@/components/app/AppModal.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { useNotification } from '@kyvg/vue3-notification'
import SettingService from '@/services/SettingService'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'
import { syncLineItemIds } from '@/utils/syncLineItemIds'
import CreditBadge from '@/components/base/CreditBadge.vue'

useHead({ title: 'Project Budget' })

const budgetId = useRoute().params.id as string || ''
const budgetService = new BudgetService()
const { notify } = useNotification()
const queryClient = useQueryClient()

queryClient.invalidateQueries()

const { data: project, isLoading: projectLoading } = useQuery({
  queryKey: ['project', budgetId],
  queryFn: () => new ProjectService().find(budgetId)
})
const { data: budget, isLoading: budgetLoading } = useQuery({
  queryKey: ['budget', budgetId],
  queryFn: async () => {
    const budget = await budgetService.find(budgetId) || new Budget()
    const lineItems = await budgetService.getLineItems(budgetId)
    budget.lineItems = lineItems
    return budget
  }
})

const { data: purchaseOrders, isLoading: poLoading } = useQuery({
  queryKey: ['purchaseOrders', budgetId],
  queryFn: async () => {
    return await new PurchaseOrderService('').find_by('budgetId', '==', budgetId) || []
  }
})

const { data: workOrders, isLoading: woLoading } = useQuery({
  queryKey: ['workOrders', budgetId],
  queryFn: async () => {
    return await new WorkOrderService('').find_by('budgetId', '==', budgetId) || []
  }
})

const isLoading = computed(() => projectLoading.value || budgetLoading.value || woLoading.value || poLoading.value || isLoadingGeneralSettings.value)

const mode = ref('active')
const showBudgetDeleteConfirmation = ref(false)
const $router = useRouter()
const budgetToUpdate = ref<Budget>(new Budget())
const deletedItems = ref([])
const showModal = ref(false)
const budgetTemplateCategory = ref(null)
const itemsValidations = ref({})
const hasUnsavedChanges = ref<boolean|null>(null)

// Helper function to clean up orphaned validation entries
const cleanupOrphanedValidations = () => {
  const currentItemIds = new Set(budgetToUpdate.value.lineItems.map(item => item.id))
  const validationIds = Object.keys(itemsValidations.value)
  
  validationIds.forEach(validationId => {
    if (!currentItemIds.has(validationId)) {
      delete itemsValidations.value[validationId]
    }
  })
}

const isBudgetItemsFormsValid = computed(() => {
  // Clean up orphaned validations before checking
  cleanupOrphanedValidations()
  return Object.values(itemsValidations.value).every((value) => value)
})

watch(budget, (value) => {
  extend(budgetToUpdate.value, JSON.parse(JSON.stringify(value)))
  defaultValues.categories = sortedCategories.value
})

// categories functions
const setCategories = (value, categoriesField) => {
  const newCategories = value.map((category, index) => {
    const newCategoryWeight = index + 1
    const newCategory = {
      ...category,
      key: `${category.name}-${newCategoryWeight}`.replace(/\s/g, '_'),
      weight: newCategoryWeight
    }
    budgetToUpdate.value.lineItems.forEach((item) => {
      if (item.category.name === newCategory.name && item.category.weight === category.weight) {
        item.category.weight = newCategory.weight
      }
    })
    return newCategory
  })
  categoriesField.handleChange(newCategories)
}

const sortedCategories = computed(() => {
  const sortedCategories = [] as { name: string, weight: number, key?: string, id?: string }[]
  budgetToUpdate.value.lineItems.forEach((item) => {
    if (!findWhere(sortedCategories, { name: item.category.name })) {
      item.category['id'] = item.category['id'] || uniqueId()

      sortedCategories.push({
        ...item.category,
        key: `${item.category.name}-${item.category.weight}`.replace(/\s/g, '_')
      })
    }
  })
  sortedCategories.sort((a, b) => a.weight - b.weight)
  return sortedCategories
})

const updateBudgetItemsCategory = ($event, oldCategoryName, index, field) => {
  budgetToUpdate.value.lineItems.forEach((item) => {
    if (item.category.name === oldCategoryName) {
      item.category.name = $event.target.value
    }
  })
  field.handleChange($event.target.value)
}

// find the highest weight and add 1
const nextCategoryWeight = () => {
  return Math.max(...categories.value.map((category) => category.weight)) + 1
}

const defaultCategoryName = () => {
  const name = 'Untitled Category'
  const categoriesNames = categories.value.map((category) => category.name)
  let i = 1
  let newName = name

  while (categoriesNames.includes(newName)) {
    newName = `${name} ${i}`
    i++
  }

  return newName
}

const addBudgetItemCategory = (categoriesField) => {
  const category = {
    name: defaultCategoryName(),
    weight: nextCategoryWeight(),
    key: `${defaultCategoryName()}-${nextCategoryWeight()}`.replace(/\s/g, '_'),
    id: uniqueId()
  }
  categoriesField.pushValue(category)
}

const deleteCategory = (items, index, categoriesField) => {
  items.forEach(item => {
    deleteItem(item.id)
  })
  categoriesField.removeValue(index)
}

// form functions
const defaultValues = reactive({
  categories: []
})

const form = useForm({
  defaultValues,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    const budget = {
      ...budgetToUpdate.value,
      lineItems: budgetToUpdate.value.lineItems.map((item) => {
        return {
          ...item,
          category: {
            name: item.category.name,
            weight: item.category.weight
          }
        }
      })
    }
    await saveBudget({
      budget: budget,
      deletedItems: deletedItems.value
    })
  }
})

const categories = form.useStore((state) => state.values.categories)
const allCategoriesHaveItems = computed(() => {
  return categories.value.every((category) => {
    return budgetToUpdate.value.lineItems.some((item) => item.category.name === category.name && item.category.weight === category.weight)
  })
})

// line items functions
const filterBudgetItemByCategory = computed(() => (category) => {
  if (!budgetToUpdate.value.lineItems) return []
  return filter(budgetToUpdate.value.lineItems, (item) => {
    return item.category.name === category.name && item.category.weight === category.weight
  }).sort((a, b) => a.weight - b.weight)
})

const sortedLineItems = computed(() => (category) => {
  return filterBudgetItemByCategory.value(category).map((item) => {
    return {
      value: item,
      id: item.id
    }
  })
})

const reOrderItems = (newItems) => {
  newItems.forEach((item, index) => {
    const budgetItem = budgetToUpdate.value.lineItems.find((lineItem) => lineItem.id === item.value.id)
    budgetItem.weight = index + 1
  })
}


const nextLineItemWeight = (category) => {
  const items = budgetToUpdate.value.lineItems.filter((item) => item.category.name === category.name && item.category.weight === category.weight)
  return items.length ? Math.max(...items.map((item) => item.weight)) + 1 : 1
}

const addBudgetItem = (categoryIndex, category) => {
  const budgetItem = new BudgetItem()
  budgetItem.id = `${new Date().getTime()}-generatedID`
  budgetItem.materialsBudget = 0
  budgetItem.laborBudget = 0
  budgetItem.category = category
  budgetItem.weight = nextLineItemWeight(category)
  budgetToUpdate.value.lineItems.push(Object.assign({}, budgetItem))
  
  // Note: The CreateBudgetItem component will emit validation status which gets registered
  // in itemsValidations. When autosave runs, the temporary ID will be replaced with a real
  // Firestore ID, and the validation sync logic in the autosave function will handle
  // transferring the validation state from the temporary ID to the new ID.
}

const deleteItem = (itemId) => {
  const index = budgetToUpdate.value.lineItems.findIndex((item) => item.id === itemId)

  if (index > -1) {
    deletedItems.value.push(itemId)
    budgetToUpdate.value.lineItems.splice(index, 1)
  }
  itemsValidations.value = omit(itemsValidations.value, itemId)
}

const showTemplatesModal = (category) => {
  budgetTemplateCategory.value = category
  showModal.value = true
}

const bugetTemplateSelected = (items: BudgetItem[]) => {
  items.forEach((item) => {
    const budgetItem = Object.assign({}, JSON.parse(JSON.stringify(item)))

    if ('_highlightResult' in budgetItem) {
      delete budgetItem._highlightResult
    }

    budgetItem.id = `${new Date().getTime() + uniqueId()}-generatedID`
    budgetItem.category = budgetTemplateCategory.value
    budgetItem.weight = nextLineItemWeight(budgetTemplateCategory.value)
    budgetToUpdate.value.lineItems.push(budgetItem)
    
    // Note: Similar to addBudgetItem, these template items will get temporary IDs
    // that will be synced with real Firestore IDs during autosave
  })
  showModal.value = false
}

// labor and material budget
const materialBudget = computed(() => {
  let materialsCost = 0
  budgetToUpdate.value.lineItems.map((item) => {
    if (item.selected && !item.isCreditLineItem) {
      materialsCost += item.materialsBudget ? toNumber(item.materialsBudget) : 0
    }
  })
  return (isNaN(materialsCost) || (materialsCost === 0)) ? 0 : materialsCost
})

const actualMaterialCost = computed(() => {
  let totalMaterialCost = 0
  purchaseOrders.value.forEach((purchaseOrder) => {
    purchaseOrder.items.forEach((item) => {
      totalMaterialCost += (item.cost * item.quantity)
    })
  })

  return totalMaterialCost
})

const laborBudget = computed(() => {
  let laborCost = 0
  budgetToUpdate.value.lineItems.map((item) => {
    if (item.selected && !item.isCreditLineItem) {
      laborCost += item.laborBudget ? toNumber(item.laborBudget) : 0
    }
  })
  return (isNaN(laborCost) || (laborCost === 0)) ? 0 : laborCost
})

// costs
const actualLaborCost = computed(() => {
  let totalLaborCost = 0
  workOrders.value.forEach((workOrder) => {
    totalLaborCost += workOrder.total
  })
  return totalLaborCost
})

const totalBudget = computed(() => {
  return materialBudget.value + laborBudget.value
})

const totalBudgetRevenue = computed(() => {
  let revenue = 0
  budgetToUpdate.value.lineItems.map((item) => {
    if (item.selected) {
      revenue += toNumber(item.totalPrice)
    }
  })
  return revenue
})

const getItemActualMaterialCost = (id: string) => {
  let totalMaterialCost = 0
  purchaseOrders.value.forEach((purchaseOrder: PurchaseOrder) => {
    purchaseOrder.items.forEach((purchaseOrderItem) => {
      if (purchaseOrderItem.lineItems.includes(id)) {
        totalMaterialCost += toNumber(purchaseOrderItem.cost * purchaseOrderItem.quantity)
      }
    })
  })

  return totalMaterialCost
}

const getItemActualLaborCost = (id: string) => {
  let totalLaborCost = 0
  workOrders.value.forEach((workOrder: WorkOrder) => {
    workOrder.items.forEach((workOrderItem) => {
      if (workOrderItem.lineItems.includes(id)) {
        totalLaborCost += toNumber(workOrderItem.cost)
      }
    })
  })

  return totalLaborCost
}

const getProgress = (budgetAmount: number, actualAmount: number) => {
  if (budgetAmount === 0) return 0
  return (actualAmount / budgetAmount) * 100
}

const toggleBudgetMode = () => {
  mode.value = mode.value === 'edit' ? 'active' : 'edit'
}

const cancel = () => {
  $router.push({
    name: 'ShowProject',
    params: {
      customerId: project.value.customerId,
      projectId: project.value.id
    }
  })
}

const { mutateAsync: saveBudget } = useMutation({
  mutationFn: (payload: {budget: Budget, deletedItems: string[]}) => budgetService.updateBudget(payload.budget, payload.deletedItems),
  onSuccess: (updatedBudget: Budget) => {
    queryClient.setQueryData(['budget', updatedBudget.id], updatedBudget)
    notify({
      type: 'success',
      title: 'Budget updated',
      text: 'Budget updated successfully'
    })
    mode.value = 'active'
    hasUnsavedChanges.value = false
    // $router.push({ name: 'ShowBudget', params: { id: budgetId } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to update the budget'
    })
  }
})

const isNotAllowedStatus = computed(() => {
  return project.value.status !== PROJECT_STATUS.LEAD && project.value.status !== PROJECT_STATUS.ESTIMATING && project.value.status !== PROJECT_STATUS.AWAITING_ACCEPTANCE
})

const { mutateAsync: deleteBudget, isPending: isDeleting } = useMutation({
  mutationFn: (payload :{budget: Budget, customerId: string}) => budgetService.deleteBudget(payload.budget, payload.customerId),
  onSuccess: async () => {
    await queryClient.invalidateQueries()
    notify({
      type: 'success',
      title: 'Budget deleted',
      text: 'Budget deleted successfully'
    })
    $router.push({ name: 'ShowBudget', params: { id: budgetId } })
    showBudgetDeleteConfirmation.value = false
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to delete the budget'
    })
    showBudgetDeleteConfirmation.value = false
  }
})

// auto save functionality
const { data:generalSettings, isLoading:isLoadingGeneralSettings } = useQuery({
  queryKey: ['settings', 'generalSettings'],
  queryFn: async () => {
    const settings = await new SettingService().find('generalSettings')
    return settings
  }
})

let autoSaveTimeout: ReturnType<typeof setInterval>
const autoSaving = ref(false)
const lastAutoSave = ref<Date | null>(null)
const isAutoSaveEnabled = ref(true)

watch(() => mode.value, (newMode) => {
  // Auto-save is only enabled during ESTIMATING status to prevent accidental changes after project approval
  if (newMode === 'edit' && project.value.status == PROJECT_STATUS.ESTIMATING) {
    isAutoSaveEnabled.value = Object.hasOwn(generalSettings.value, 'enableBudgetAutoSave') ? generalSettings.value.enableBudgetAutoSave : true
    const waitTime = generalSettings.value.autoSaveWaitTime || 10

    if (!isAutoSaveEnabled.value) {
      return
    }

    clearInterval(autoSaveTimeout)
    autoSaveTimeout = setInterval(async () => {
      if (categories.value.length > 0) {
        autoSaving.value = true
        lastAutoSave.value = null
        try {
          const savedBudget = await budgetService.updateBudget({
            ...budgetToUpdate.value,
            lineItems: budgetToUpdate.value.lineItems.map((item) => {
              return {
                ...item,
                category: {
                  name: item.category.name,
                  weight: item.category.weight
                }
              }
            })
          }, deletedItems.value)

          // update the budgetToUpdate lineItems ids
          const updatedLineItems = syncLineItemIds(budgetToUpdate.value.lineItems, savedBudget.lineItems)
          
          // Clean up orphaned validation entries and sync with new IDs
          const newValidations = {}
          updatedLineItems.forEach((item, index) => {
            const oldItem = budgetToUpdate.value.lineItems[index]
            if (oldItem && oldItem.id !== item.id && itemsValidations.value[oldItem.id] !== undefined) {
              // Transfer validation from old ID to new ID
              newValidations[item.id] = itemsValidations.value[oldItem.id]
              // Remove the orphaned validation entry
              delete itemsValidations.value[oldItem.id]
            } else if (itemsValidations.value[item.id] !== undefined) {
              // Keep existing validation for items that didn't change IDs
              newValidations[item.id] = itemsValidations.value[item.id]
            }
          })
          
          // Update itemsValidations with cleaned up entries
          itemsValidations.value = { ...newValidations }
          budgetToUpdate.value.lineItems = updatedLineItems

          lastAutoSave.value = new Date()
        } catch (error) {
          notify({
            type: 'error',
            title: 'Auto save error',
            text: error.message || 'An error occurred while auto saving the budget'
          })
        } finally {
          autoSaving.value = false
        }
      }
    }, waitTime * 1000)
  } else {
    clearInterval(autoSaveTimeout)
  }
})


onBeforeUnmount(() => {
  clearInterval(autoSaveTimeout)
})


watch(() => mode.value, (newMode) => {
  if (newMode === 'edit') {
    hasUnsavedChanges.value = true
  } else {
    hasUnsavedChanges.value = null
  }
})


// prevent user from navigating away without saving
useUnsavedChangesWarning(hasUnsavedChanges)

</script>
